import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownRight,
  ArrowUpRight,
  PiggyBank,
  Landmark,
  Target,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/currency";
import MoneyAmount from "@/components/MoneyAmount";
import TransactionDialog from "@/components/TransactionDialog";
import VoiceExpenseDialog from "@/components/VoiceExpenseDialog";
import ReceiptScanDialog from "@/components/ReceiptScanDialog";
import Heatmap from "@/components/Heatmap";
import BillReminders from "@/components/BillReminders";
import MonthPicker, { currentMonth } from "@/components/MonthPicker";
import { useDisplayMoney } from "@/components/CurrencyToggle";
import ChartTooltip from "@/components/ChartTooltip";
import QuickActions from "@/components/QuickActions";
import AIInsightCard from "@/components/AIInsightCard";
import RecurringOverview from "@/components/RecurringOverview";
import AdBanner, { AD_SLOTS } from "@/components/AdBanner";
import { toast } from "sonner";

const ACCENTS = {
  primary:  { value: "text-foreground",                            color: "hsl(var(--primary))", glow: "rgba(30,63,51,0.10)",   iconBg: "linear-gradient(160deg,#1E3F33 0%,#4A6B53 100%)" },
  income:   { value: "text-emerald-700 dark:text-emerald-400",     color: "#16A34A",              glow: "rgba(22,163,74,0.12)",  iconBg: "linear-gradient(160deg,#047857 0%,#16A34A 100%)" },
  expense:  { value: "text-[#B91C1C] dark:text-[#FCA5A5]",         color: "#EF4444",              glow: "rgba(239,68,68,0.12)",  iconBg: "linear-gradient(160deg,#B91C1C 0%,#EF4444 100%)" },
  savings:  { value: "text-[#2563EB] dark:text-[#93C5FD]",         color: "#3B82F6",              glow: "rgba(59,130,246,0.12)", iconBg: "linear-gradient(160deg,#2563EB 0%,#3B82F6 100%)" },
  budget:   { value: "text-[#7C3AED] dark:text-[#C4B5FD]",         color: "#8B5CF6",              glow: "rgba(139,92,246,0.14)", iconBg: "linear-gradient(160deg,#6D28D9 0%,#8B5CF6 100%)" },
};

function StatCard({ label, value, hint, icon: Icon, accent = "primary", testid, span, progress, onClick }) {
  const a = ACCENTS[accent] || ACCENTS.primary;
  const clickable = typeof onClick === "function";
  const handleKey = clickable
    ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }
    : undefined;
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      onKeyDown={handleKey}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`pcard pcard-hover pcard-tint relative p-6 md:p-7 ${clickable ? "cursor-pointer transition-[box-shadow,filter] duration-200 ease-out hover:brightness-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50" : ""} ${span || ""}`}
      style={{ "--pcard-accent": a.color, "--pcard-glow": a.glow }}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
          <div className={`mt-2.5 p-count-up font-display text-[26px] font-extrabold tabular-nums tracking-tight md:text-[30px] ${a.value}`}>
            {value}
          </div>
          {hint && <div className="mt-1 text-[12px] text-muted-foreground">{hint}</div>}
        </div>
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-white"
          style={{
            background: a.iconBg,
            boxShadow: "0 10px 20px -10px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.3)",
          }}
        >
          <Icon className="h-4.5 w-4.5" strokeWidth={2.2} />
        </div>
      </div>
      {progress != null && (
        <div className="mt-5 pbar" style={{ "--pbar-color": a.color }}>
          <div className="pbar-fill" style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>
      )}
    </div>
  );
}

const CHART_COLORS = ["#1E3F33", "#4A6B53", "#D98E36", "#C05C4F", "#2F6B82", "#7C5295", "#B5572E", "#5C5A55", "#7AA188", "#A05D40"];

export default function Dashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const currency = user?.currency || "USD";

  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [periodData, setPeriodData] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const displayCurrency = currency;
  const { fmt } = useDisplayMoney(displayCurrency);
  const [openExpense, setOpenExpense] = useState(false);
  const [openIncome, setOpenIncome] = useState(false);
  const [openVoice, setOpenVoice] = useState(false);
  const [openReceipt, setOpenReceipt] = useState(false);
  const [categories, setCategories] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [highlightRecent, setHighlightRecent] = useState(false);
  const [recurringRefreshKey, setRecurringRefreshKey] = useState(0);
  const navigate = useNavigate();

  // Convert `YYYY-MM` → { from: 'YYYY-MM-01', to: 'YYYY-MM-<last>' }
  const monthRange = (m) => {
    const [y, mo] = m.split("-").map(Number);
    const from = `${m}-01`;
    const last = new Date(y, mo, 0).getDate();
    const to = `${m}-${String(last).padStart(2, "0")}`;
    return { from, to };
  };

  const goToSection = (kind) => {
    const { from, to } = monthRange(month);
    if (kind === "balance") {
      // Same-page scroll + brief highlight of Recent Transactions
      const el = document.getElementById("dashboard-recent");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setHighlightRecent(true);
      setTimeout(() => setHighlightRecent(false), 1400);
    } else if (kind === "income") {
      navigate(`/incomes?from=${from}&to=${to}#history`);
    } else if (kind === "expense") {
      navigate(`/expenses?from=${from}&to=${to}#history`);
    } else if (kind === "savings") {
      navigate(`/insights#savings-analysis`);
    }
  };

  const fetchAll = async () => {
    const [s, t, c, h] = await Promise.all([
      api.get(`/dashboard/summary?month=${month}`),
      api.get("/dashboard/trend?months=6"),
      api.get("/categories"),
      api.get("/dashboard/heatmap?days=91"),
    ]);
    setSummary(s.data);
    setTrend(t.data.series || []);
    setCategories(c.data || []);
    setHeatmap(h.data?.days || []);
  };

  const fetchPeriod = async (range) => {
    const { data } = await api.get(`/dashboard/period?range=${range}`);
    setPeriodData(data);
  };

  useEffect(() => { fetchAll();   }, [month]);
  useEffect(() => { fetchPeriod(period); }, [period]);

  // Auto-materialize any missing recurring occurrences (up to today) once per session.
  // The backend `/api/recurring/run` is idempotent via `last_run_date`, so re-calls are safe.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/recurring/run");
        if (cancelled) return;
        const created = Number(data?.created || 0);
        if (created > 0) {
          toast.success(
            created === 1
              ? "1 recurring transaction was auto-posted"
              : `${created} recurring transactions were auto-posted`
          );
          // Refresh dashboard + recurring overview so newly-posted entries show up immediately
          fetchAll();
          setRecurringRefreshKey((k) => k + 1);
        }
      } catch {
        /* silent: recurring auto-run is a background convenience, not user-blocking */
      }
    })();
    return () => { cancelled = true; };
     
  }, []);

  const pieData = useMemo(
    () => (summary?.expense_by_category || []).map((c, i) => ({
      name: c.category,
      value: Number(c.total),
      color: CHART_COLORS[i % CHART_COLORS.length],
    })),
    [summary],
  );

  const incVsExp = useMemo(
    () => trend.map((m) => ({ month: m.month.slice(5), Income: m.income, Expense: m.expense })),
    [trend],
  );

  const trendData = useMemo(
    () => trend.map((m) => ({ month: m.month.slice(5), Savings: m.savings })),
    [trend],
  );

  if (!summary) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const balance = summary.balance ?? summary.income - summary.expense;
  const budgetPct = summary.budget > 0 ? Math.min((summary.expense / summary.budget) * 100, 999) : 0;

  return (
    <div className="space-y-6 md:space-y-7" data-testid="dashboard-root">
      {/* Month header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("overview")}</div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
              {new Date(`${month}-01T00:00:00Z`).toLocaleString(i18n.language, { month: "long", year: "numeric" })}
            </h1>
            <MonthPicker value={month} onChange={setMonth} />
          </div>
        </div>
      </div>

      {/* Premium Quick Actions */}
      <QuickActions
        onVoice={() => setOpenVoice(true)}
        onScan={() => setOpenReceipt(true)}
        onIncome={() => setOpenIncome(true)}
        onExpense={() => setOpenExpense(true)}
      />

      <BillReminders />

      {/* AI Insight CTA */}
      <AIInsightCard />

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          testid="dashboard-balance-card"
          label={t("balance")}
          value={<MoneyAmount preformatted={fmt(balance)} value={balance} tone="auto" />}
          hint={t("balance_hint")}
          icon={Landmark}
          accent="primary"
          onClick={() => goToSection("balance")}
        />
        <StatCard
          testid="dashboard-income-card"
          label={t("income")}
          value={<MoneyAmount preformatted={fmt(summary.income)} value={summary.income} tone="neutral" />}
          icon={ArrowUpRight}
          accent="income"
          onClick={() => goToSection("income")}
        />
        <StatCard
          testid="dashboard-expense-card"
          label={t("expenses")}
          value={<MoneyAmount preformatted={fmt(summary.expense)} value={summary.expense} tone="neutral" />}
          icon={ArrowDownRight}
          accent="expense"
          onClick={() => goToSection("expense")}
        />
        <StatCard
          testid="dashboard-savings-card"
          label={t("savings")}
          value={<MoneyAmount preformatted={fmt(summary.savings)} value={summary.savings} tone="neutral" />}
          icon={PiggyBank}
          accent="savings"
          onClick={() => goToSection("savings")}
        />
      </div>

      {/* AdSense — placed below the summary cards, above budget/transactions */}
      <AdBanner slot={AD_SLOTS.dashboard} testid="ad-dashboard" />

      {/* Budget detail */}
      <div data-testid="dashboard-budget-card" className="pcard pcard-hover p-6 md:p-7">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("monthly_budget")}</div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="font-display text-3xl font-extrabold tracking-tight tabular-nums">
                {summary.budget > 0 ? <MoneyAmount value={summary.budget_remaining} currency={currency} tone="auto" /> : "—"}
              </span>
              <span className="text-sm font-medium text-muted-foreground">
                {summary.budget > 0 ? t("of_remaining", { amount: formatMoney(summary.budget, currency) }) : t("no_budget_set")}
              </span>
            </div>
          </div>
          <Link
            to="/budget"
            aria-label={summary.budget > 0 ? t("manage_monthly_budget") : t("set_monthly_budget")}
            data-testid="dashboard-budget-cta"
            className="group inline-flex items-center gap-2 rounded-full px-1 py-0.5 outline-none transition-opacity duration-150 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            <Target className="h-4 w-4 text-primary" />
            <Badge variant="secondary" className="rounded-full text-[11.5px] font-semibold cursor-pointer">
              {summary.budget > 0 ? t("percent_used", { n: budgetPct.toFixed(0) }) : t("set_one_in_budget")}
            </Badge>
          </Link>
        </div>
        {summary.budget > 0 && (
          <div
            className="mt-5 pbar"
            style={{
              "--pbar-color":
                budgetPct > 100 ? "#EF4444" : budgetPct > 80 ? "#D98E36" : "#16A34A",
            }}
          >
            <div className="pbar-fill" style={{ width: `${Math.min(budgetPct, 100)}%` }} />
          </div>
        )}
      </div>

      {/* Recurring Overview — sits directly below the Monthly Budget card */}
      <RecurringOverview refreshKey={recurringRefreshKey} />

      {/* Period summary */}
      <div className="pcard pcard-hover p-6 md:p-7">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("period_summary")}</div>
            <div className="font-display text-xl font-bold tracking-tight">{t("period_summary_sub")}</div>
          </div>
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList data-testid="period-tabs">
              <TabsTrigger value="daily" data-testid="period-daily">{t("daily")}</TabsTrigger>
              <TabsTrigger value="weekly" data-testid="period-weekly">{t("weekly")}</TabsTrigger>
              <TabsTrigger value="monthly" data-testid="period-monthly">{t("monthly")}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-border/70 bg-background/40 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("income")}</div>
            <div className="mt-1 font-display text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
              <MoneyAmount value={periodData?.income || 0} currency={currency} tone="neutral" />
            </div>
          </div>
          <div className="rounded-2xl border border-border/70 bg-background/40 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("expenses")}</div>
            <div className="mt-1 font-display text-2xl font-bold tabular-nums text-[#B91C1C] dark:text-[#FCA5A5]">
              <MoneyAmount value={periodData?.expense || 0} currency={currency} tone="neutral" />
            </div>
          </div>
          <div className="rounded-2xl border border-border/70 bg-background/40 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("saved")}</div>
            <div className="mt-1 font-display text-2xl font-bold tabular-nums text-primary">
              <MoneyAmount value={periodData?.savings || 0} currency={currency} tone="neutral" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="pcard pcard-hover p-6 md:p-7">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("expense_by_category")}</div>
          <div className="font-display text-xl font-bold tracking-tight">{t("this_month")}</div>
          <div className="mt-4 h-80">
            {pieData.length === 0 ? (
              <EmptyChart label={t("empty_chart_expense")} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={68} outerRadius={110} paddingAngle={3} animationDuration={900}>
                    {pieData.map((entry, i) => (<Cell key={i} fill={entry.color} stroke="hsl(var(--card))" strokeWidth={3} />))}
                  </Pie>
                  <Tooltip
                    content={
                      <ChartTooltip
                        currency={displayCurrency}
                        totalForPercent={pieData.reduce((s, d) => s + d.value, 0)}
                      />
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="pcard pcard-hover p-6 md:p-7">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("income_vs_expense")}</div>
          <div className="font-display text-xl font-bold tracking-tight">{t("last_6_months")}</div>
          <div className="mt-4 h-80">
            {incVsExp.every((d) => d.Income === 0 && d.Expense === 0) ? (
              <EmptyChart label={t("empty_chart_compare")} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={incVsExp} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => formatMoney(v, currency, { compact: true })} />
                  <Tooltip content={<ChartTooltip currency={displayCurrency} />} cursor={{ fill: "hsl(var(--accent) / 0.4)" }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                  <Bar dataKey="Income" fill="#16A34A" radius={[8, 8, 0, 0]} animationDuration={900} />
                  <Bar dataKey="Expense" fill="#EF4444" radius={[8, 8, 0, 0]} animationDuration={900} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div
        id="dashboard-recent"
        className={`pcard pcard-hover p-6 md:p-7 transition-all duration-300 ${highlightRecent ? "ring-2 ring-primary/60 ring-offset-2 ring-offset-background shadow-[0_18px_40px_-18px_hsl(var(--primary)/0.45)]" : ""}`}
        data-testid="recent-transactions-card"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("recent_transactions")}</div>
            <div className="font-display text-xl font-bold tracking-tight">{t("latest_activity")}</div>
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          {summary.recent_transactions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
              {t("empty_transactions")}
            </div>
          ) : (
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  <th className="py-3 pr-4 font-semibold">{t("th_date")}</th>
                  <th className="py-3 pr-4 font-semibold">{t("th_category_source")}</th>
                  <th className="py-3 pr-4 font-semibold">{t("th_notes")}</th>
                  <th className="py-3 pr-4 font-semibold text-right">{t("th_amount")}</th>
                </tr>
              </thead>
              <tbody>
                {summary.recent_transactions.map((tx, i) => (
                  <tr key={(tx.id || "") + i} className="border-t border-border/70 transition-colors hover:bg-muted/40" data-testid="recent-transaction-row">
                    <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">
                      {new Date(tx.date).toLocaleDateString(i18n.language)}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-xs font-semibold ${tx.kind === "income" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-[#FEE2E2] text-[#B91C1C] dark:bg-rose-900/30 dark:text-rose-300"}`}>
                        {tx.kind === "income" ? t("income") : t("expenses")} · {tx.category}
                      </span>
                    </td>
                    <td className="py-3 pr-4 max-w-xs truncate text-muted-foreground">{tx.notes || "—"}</td>
                    <td className={`py-3 pr-4 text-right font-display text-base font-bold tabular-nums ${tx.kind === "income" ? "text-emerald-700 dark:text-emerald-400" : "text-[#B91C1C] dark:text-[#FCA5A5]"}`}>
                      <MoneyAmount value={tx.kind === "income" ? tx.amount : -tx.amount} currency={currency} tone="neutral" signed />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="pcard pcard-hover p-6 md:p-7">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("savings_trend")}</div>
        <div className="font-display text-xl font-bold tracking-tight">{t("last_6_months")}</div>
        <div className="mt-4 h-72">
          {trendData.every((d) => d.Savings === 0) ? (
            <EmptyChart label={t("empty_chart_savings")} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="sv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E3F33" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#1E3F33" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => formatMoney(v, currency, { compact: true })} />
                <Tooltip content={<ChartTooltip currency={displayCurrency} />} cursor={{ stroke: "hsl(var(--border))" }} />
                <Area type="monotone" dataKey="Savings" stroke="#1E3F33" strokeWidth={2.5} fill="url(#sv)" animationDuration={900} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <Heatmap data={heatmap} weeks={13} />

      <TransactionDialog
        open={openExpense}
        onOpenChange={setOpenExpense}
        kind="expense"
        categories={categories}
        onSaved={fetchAll}
      />
      <TransactionDialog
        open={openIncome}
        onOpenChange={setOpenIncome}
        kind="income"
        categories={categories}
        onSaved={fetchAll}
      />
      <VoiceExpenseDialog open={openVoice} onOpenChange={setOpenVoice} onCreated={fetchAll} />
      <ReceiptScanDialog open={openReceipt} onOpenChange={setOpenReceipt} onCreated={fetchAll} />
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
      {label}
    </div>
  );
}
